import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

export default function AdminDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    const [s, v, o] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/vendors"), api.get("/admin/orders")
    ]);
    setStats(s.data); setVendors(v.data); setOrders(o.data);
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/auth"); return; }
    if (user.role !== "admin") { navigate("/"); return; }
    load();
    // eslint-disable-next-line
  }, [user, loading]);

  const act = async (vid, action) => {
    await api.post(`/admin/vendors/${vid}/${action}`);
    toast.success(`Vendor ${action}d`);
    load();
  };

  if (!stats) return <div className="max-w-7xl mx-auto px-6 py-16 text-[#718096]">Loading…</div>;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="mb-8">
        <div className="label-caps mb-2">Admin console</div>
        <h1 className="text-4xl">Platform overview</h1>
      </div>

      <div className="flex gap-2 mb-6">
        {["overview", "vendors", "orders"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
            className={`px-4 py-2 rounded-full text-sm font-semibold border ${tab === t ? "bg-[#13294B] text-white border-[#13294B]" : "bg-white border-[#E2DCD0] text-[#13294B]"}`}>
            {t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Users" value={stats.users} />
          <StatCard label="Vendors approved" value={stats.approved_vendors} />
          <StatCard label="Pending vendors" value={stats.pending_vendors} />
          <StatCard label="Products live" value={stats.products} />
          <StatCard label="Total orders" value={stats.orders} />
          <StatCard label="Platform revenue" value={`₹${stats.revenue.toLocaleString('en-IN')}`} />
          <StatCard label="Commission (5%)" value={`₹${(stats.revenue * 0.05).toFixed(0)}`} />
          <StatCard label="Total vendors" value={stats.vendors} />
        </div>
      )}

      {tab === "vendors" && (
        <div className="card-flat overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F2EFEB]">
              <tr>
                <th className="p-4 text-left label-caps">Business</th>
                <th className="p-4 text-left label-caps">Location</th>
                <th className="p-4 text-left label-caps">Status</th>
                <th className="p-4 text-right label-caps">Actions</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map((v) => (
                <tr key={v.id} className="border-t border-[#E2DCD0]" data-testid={`admin-vendor-${v.id}`}>
                  <td className="p-4">
                    <div className="font-semibold">{v.business_name}</div>
                    <div className="text-xs text-[#718096] line-clamp-1">{v.description}</div>
                  </td>
                  <td className="p-4 text-[#4A5568]">{v.location}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${
                      v.status === "approved" ? "bg-[#EAF3EE] text-[#275F36]" :
                      v.status === "rejected" ? "bg-[#F8E4DE] text-[#B44618]" :
                      "bg-[#FFF3EA] text-[#B44618]"
                    }`}>{v.status}</span>
                  </td>
                  <td className="p-4 text-right">
                    {v.status === "pending" && (
                      <div className="inline-flex gap-2">
                        <button onClick={() => act(v.id, "approve")} data-testid={`approve-${v.id}`} className="p-2 rounded-full bg-[#EAF3EE] text-[#275F36] hover:bg-[#275F36] hover:text-white transition-colors"><Check className="w-4 h-4" /></button>
                        <button onClick={() => act(v.id, "reject")} data-testid={`reject-${v.id}`} className="p-2 rounded-full bg-[#F8E4DE] text-[#B44618] hover:bg-[#B44618] hover:text-white transition-colors"><X className="w-4 h-4" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {vendors.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-[#718096]">No vendors yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "orders" && (
        <div className="card-flat overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F2EFEB]">
              <tr>
                <th className="p-4 text-left label-caps">Order</th>
                <th className="p-4 text-left label-caps">Buyer</th>
                <th className="p-4 text-left label-caps">Total</th>
                <th className="p-4 text-left label-caps">Status</th>
                <th className="p-4 text-left label-caps">Date</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-[#E2DCD0]">
                  <td className="p-4 font-mono text-xs">#{o.id.slice(0, 8)}</td>
                  <td className="p-4">{o.buyer_name}</td>
                  <td className="p-4 font-semibold">₹{o.total.toLocaleString('en-IN')}</td>
                  <td className="p-4"><span className="px-2 py-1 rounded bg-[#F2EFEB] text-xs uppercase font-bold">{o.status}</span></td>
                  <td className="p-4 text-[#718096] text-xs">{new Date(o.created_at).toLocaleString('en-IN')}</td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#718096]">No orders yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="card-flat p-6">
      <div className="label-caps">{label}</div>
      <div className="text-3xl font-bold mt-2 font-serif text-[#13294B]">{value}</div>
    </div>
  );
}
